import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { stickerPacksApi } from '../../api/stickerPacks';
import { filesApi, getCachedBlobUrl, createBlobUrl } from '../../api/files';
import { LOCAL_STICKER_PACKS } from '../../api/stickerPacks.mock';
import '../../css/chats/StickerPicker.css';

/**
 * Проверяет, является ли imageFileId локальным путём (а не UUID).
 * Локальные пути начинаются с '/' — это статические SVG из public/stickers/.
 */
function isLocalPath(fileId) {
    return typeof fileId === 'string' && fileId.startsWith('/');
}

/**
 * Компонент одного стикера: загружает изображение через files API,
 * кэширует blob URL для переиспользования.
 *
 * Для локальных путей (/stickers/...) используется прямая загрузка
 * без авторизации — это статические SVG из public/.
 *
 * @param {Object} sticker - { id, imageFileId, emoji? }
 * @param {Function} onSelect - (sticker) => void
 */
function StickerItem({ sticker, onSelect }) {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const fileId = sticker.imageFileId || sticker.fileId;

    useEffect(() => {
        if (!fileId) {
            setLoading(false);
            setError(true);
            return;
        }
        let cancelled = false;

        // Локальный путь — прямой URL к статике, без files API
        if (isLocalPath(fileId)) {
            setUrl(fileId);
            setLoading(false);
            return;
        }

        const loadSticker = async () => {
            // Проверяем кэш
            const cached = getCachedBlobUrl(fileId);
            if (cached) {
                if (!cancelled) {
                    setUrl(cached.url);
                    setLoading(false);
                }
                return;
            }

            try {
                const blob = await filesApi.downloadImage(fileId, { retries: 3, retryDelay: 1500 });
                if (cancelled || blob.size === 0) {
                    if (!cancelled) setError(true);
                    return;
                }
                const stickerBlob = new Blob([blob], { type: blob.type || 'image/png' });
                const blobUrl = createBlobUrl(fileId, stickerBlob);
                if (!cancelled) {
                    setUrl(blobUrl);
                    setLoading(false);
                }
            } catch (err) {
                if (err.response?.status !== 429) {
                    console.error('[StickerItem] Failed to load sticker:', err.message);
                }
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadSticker();
        return () => { cancelled = true; };
    }, [fileId]);

    return (
        <button
            type="button"
            className="sticker-picker__sticker"
            onClick={() => onSelect(sticker)}
            title={sticker.emoji || ''}
            disabled={loading || error}
        >
            {loading && <span className="sticker-picker__sticker-placeholder">⏳</span>}
            {error && <span className="sticker-picker__sticker-placeholder">❌</span>}
            {url && <img src={url} alt={sticker.emoji || 'Стикер'} loading="lazy" />}
        </button>
    );
}

/**
 * Панель выбора стикеров.
 * Загружает стикерпаки с сервера, отображает сетку стикеров с табами паков.
 *
 * Рендерится через portal в document.body с фиксированным позиционированием
 * относительно anchor-элемента (кнопка, открывшая пикер).
 *
 * @param {React.RefObject} anchorRef - Ref на элемент-якорь
 * @param {Function} onSelect - Колбэк при выборе стикера: (sticker) => void
 * @param {Function} onClose - Колбэк при закрытии пикера
 */
export default function StickerPicker({ anchorRef, onSelect, onClose }) {
    const [packs, setPacks] = useState([]);
    const [activePackId, setActivePackId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [coords, setCoords] = useState(null);
    const containerRef = useRef(null);

    const PICKER_WIDTH = 340;
    const GAP = 8;
    const MARGIN = 8;

    // Загрузка стикерпаков (с fallback на локальные при ошибке API)
    useEffect(() => {
        let cancelled = false;
        const fetchPacks = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await stickerPacksApi.getStickerPacksAll();
                if (cancelled) return;
                if (data && data.length > 0) {
                    setPacks(data);
                    setActivePackId(data[0].id);
                } else {
                    // API вернул пустой список — используем локальные
                    setPacks(LOCAL_STICKER_PACKS);
                    setActivePackId(LOCAL_STICKER_PACKS[0].id);
                }
            } catch (err) {
                // API недоступен — fallback на локальные стикеры
                if (!cancelled) {
                    setPacks(LOCAL_STICKER_PACKS);
                    setActivePackId(LOCAL_STICKER_PACKS[0].id);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchPacks();
        return () => { cancelled = true; };
    }, []);

    // Вычисление позиции (как в EmojiPicker)
    const computePosition = useCallback(() => {
        const anchor = anchorRef?.current;
        if (!anchor) return null;

        const rect = anchor.getBoundingClientRect();
        const pickerEl = containerRef.current;
        const pickerHeight = pickerEl ? pickerEl.offsetHeight : 380;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const spaceAbove = rect.top;
        const spaceBelow = vh - rect.bottom;

        let top;
        if (spaceAbove >= pickerHeight + GAP) {
            top = rect.top - pickerHeight - GAP;
        } else if (spaceBelow >= pickerHeight + GAP) {
            top = rect.bottom + GAP;
        } else if (spaceAbove >= spaceBelow) {
            top = MARGIN;
        } else {
            top = Math.max(MARGIN, vh - pickerHeight - MARGIN);
        }

        let left = rect.left;
        if (left + PICKER_WIDTH > vw - MARGIN) {
            left = vw - PICKER_WIDTH - MARGIN;
        }
        if (left < MARGIN) left = MARGIN;

        return { top, left };
    }, [anchorRef]);

    useLayoutEffect(() => {
        setCoords(computePosition());
    }, [computePosition]);

    useLayoutEffect(() => {
        if (coords === null) return;
        const pos = computePosition();
        if (pos) setCoords(pos);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coords === null]);

    // Пересчёт при scroll/resize
    useEffect(() => {
        const handle = () => {
            const pos = computePosition();
            if (pos) setCoords(pos);
        };
        window.addEventListener('scroll', handle, { capture: true, passive: true });
        window.addEventListener('resize', handle);
        return () => {
            window.removeEventListener('scroll', handle, { capture: true, passive: true });
            window.removeEventListener('resize', handle);
        };
    }, [computePosition]);

    // Закрытие по клику вне и Escape
    useEffect(() => {
        if (!onClose) return;
        const handlePointerDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handlePointerDown);
            document.addEventListener('touchstart', handlePointerDown);
            document.addEventListener('keydown', handleKeyDown);
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const handleSelect = useCallback((sticker) => {
        onSelect?.(sticker);
        onClose?.();
    }, [onSelect, onClose]);

    const activePack = packs.find(p => p.id === activePackId);
    const stickers = activePack?.stickers || [];

    const pickerStyle = coords
        ? { position: 'fixed', top: `${coords.top}px`, left: `${coords.left}px` }
        : { position: 'fixed', top: 0, left: 0, visibility: 'hidden' };

    return createPortal(
        <div
            ref={containerRef}
            className="sticker-picker"
            style={pickerStyle}
        >
            {loading && (
                <div className="sticker-picker__loading">Загрузка стикеров…</div>
            )}
            {error && (
                <div className="sticker-picker__error">{error}</div>
            )}
            {!loading && !error && packs.length === 0 && (
                <div className="sticker-picker__empty">Стикерпаки пока недоступны</div>
            )}
            {!loading && !error && packs.length > 0 && (
                <>
                    {/* Сетка стикеров активного пака */}
                    <div className="sticker-picker__grid">
                        {stickers.length === 0 ? (
                            <div className="sticker-picker__empty">В этом паке нет стикеров</div>
                        ) : (
                            stickers.map(sticker => (
                                <StickerItem
                                    key={sticker.id}
                                    sticker={sticker}
                                    onSelect={handleSelect}
                                />
                            ))
                        )}
                    </div>

                    {/* Табы паков */}
                    <div className="sticker-picker__packs">
                        {packs.map(pack => (
                            <button
                                key={pack.id}
                                type="button"
                                className={`sticker-picker__pack ${activePackId === pack.id ? 'sticker-picker__pack--active' : ''}`}
                                onClick={() => setActivePackId(pack.id)}
                                title={pack.name}
                            >
                                {pack.icon || pack.name?.charAt(0) || '📦'}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>,
        document.body
    );
}
